import React, { memo, useCallback } from 'react';
import { TextField, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useCopyToClipboard } from 'react-use';
import { GlobalMessage, MessageType } from '@alycecom/services';
import { Icon } from '@alycecom/ui';

import { getCanBeDonated, getCanExchangeAndDonate, getGiftGl } from '../../../store/data/data.selectors';
import StyledSubmitButton from '../../../../../components/Shared/StyledSubmitButton';
import StyledSecondaryButton from '../../../../../components/Shared/StyledSecondaryButton';
import { IAlyceCustomTheme } from '../../../../../styles/alyceTheme';
import { getIsActivate } from '../../../store/statuses/statuses.selectors';
import { skipDetectCountryFlow } from '../../../../../store/geoIpCountry/geoIpCountry.actions';
import { getSenderState } from '../../../../../store/users/users.selectors';

import ButtonsDivider from './ButtonsDivider';
import FlowWrapper from './FlowWrapper';

const useStyles = makeStyles<IAlyceCustomTheme>(({ palette, spacing }) => ({
  buttonText: {
    lineHeight: '1.15 !important',
    color: `${palette.customStyling.primaryCtaColor} !important`,
    marginLeft: spacing(1),
  },
  senderMessageHint: {
    fontFamily: 'Lato',
    fontSize: 14,
    color: palette.text.primary,
    textAlign: 'center',
  },
}));

export interface IWrongCountryFlowProps {
  onDonate: () => void;
}

const WrongCountryFlow = ({ onDonate }: IWrongCountryFlowProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const gl = useSelector(getGiftGl);
  const sender = useSelector(getSenderState);
  const isActivate = useSelector(getIsActivate);
  const canBeDonated = useSelector(getCanBeDonated);
  const canExchangeAndDonate = useSelector(getCanExchangeAndDonate);

  const isDonationAllowed = canBeDonated && !isActivate;
  const isExchangeAndDonateAllowed = canExchangeAndDonate && !isActivate;

  const [, copyToClipBoard] = useCopyToClipboard();
  const { showGlobalMessage } = GlobalMessage.useGlobalMessage();

  const handleCopyEmail = useCallback(() => {
    if (sender) {
      copyToClipBoard(sender.email);
      showGlobalMessage({ text: 'Email is copied!', type: MessageType.Success });
    }
  }, [sender, copyToClipBoard, showGlobalMessage]);

  const handleContinueAnyway = useCallback(() => {
    dispatch(skipDetectCountryFlow());
  }, [dispatch]);

  const handleDonate = useCallback(() => {
    onDonate();
  }, [onDonate]);

  return (
    <FlowWrapper>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={12}>
          <Typography className={classes.senderMessageHint}>Please get in touch with the sender</Typography>
        </Grid>
        <Grid item sm={8} xs={12}>
          <TextField name="senderEmail" fullWidth variant="outlined" disabled value={sender?.email} />
        </Grid>
        <Grid item sm={4} xs={12}>
          <StyledSubmitButton data-testid="copyEmailButton" width="100%" onClick={handleCopyEmail} variant="outlined">
            <Icon color="customStyling.primaryCtaColor" icon="copy" />
            <span className={classes.buttonText}>Copy</span>
          </StyledSubmitButton>
        </Grid>
      </Grid>
      <ButtonsDivider />
      <Grid container direction="row" spacing={4}>
        <Grid item xs={12} sm={isExchangeAndDonateAllowed ? 6 : 12}>
          <StyledSecondaryButton onClick={handleContinueAnyway}>Continue anyway</StyledSecondaryButton>
        </Grid>
        {isDonationAllowed && (
          <Grid item xs={12} sm={isExchangeAndDonateAllowed ? 6 : 12}>
            <StyledSecondaryButton onClick={handleDonate}>Donate {gl} value</StyledSecondaryButton>
          </Grid>
        )}
      </Grid>
    </FlowWrapper>
  );
};

export default memo<IWrongCountryFlowProps>(WrongCountryFlow);
